import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Search, Send, Trash2, UserPlus, Users } from "lucide-react";
import { toast } from "sonner";
import { Gate } from "@/components/wallet/Gate";
import { useWallet } from "@/lib/wallet/store";
import { isValidAddress } from "@/lib/wallet/xrpl";
import { addressFingerprint, truncateAddress } from "@/lib/wallet/format";

export const Route = createFileRoute("/contacts")({
  staticData: { sitemap: false },
  head: () => ({
    meta: [
      { title: "Address book — Cobalt Wallet" },
      {
        name: "description",
        content:
          "Save and label XRPL recipients with their destination tags, then reuse them on the send screen without retyping an address.",
      },
      { property: "og:title", content: "Address book — Cobalt Wallet" },
      {
        property: "og:description",
        content: "Labelled XRPL recipients, stored only on this device.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: () => (
    <Gate>
      <ContactsScreen />
    </Gate>
  ),
});

function ContactsScreen() {
  const { contacts, saveContact, removeContact } = useWallet();
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [address, setAddress] = useState("");
  const [tag, setTag] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const filtered = useMemo(() => {
    const q = term.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter(
      (c) =>
        c.label.toLowerCase().includes(q) ||
        c.address.toLowerCase().includes(q) ||
        (c.note ?? "").toLowerCase().includes(q),
    );
  }, [contacts, term]);

  const reset = () => {
    setEditing(null);
    setLabel("");
    setAddress("");
    setTag("");
    setNote("");
    setOpen(false);
  };

  const submit = async () => {
    setBusy(true);
    try {
      if (!(await isValidAddress(address.trim()))) {
        throw new Error("That is not a valid XRPL address.");
      }
      saveContact({ ...(editing ? { id: editing } : {}), label, address, tag, note });
      toast.success(editing ? "Recipient updated." : "Recipient saved.");
      reset();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save the recipient.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="animate-rise space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-[18px] font-medium tracking-tight">Address book</h1>
          <p className="readout mt-1 text-[10px] text-faint">
            Labels live on this device only. Always verify a fingerprint before a first transfer.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            reset();
            setOpen(true);
          }}
          className="btn-accent grid size-9 place-items-center"
          aria-label="Add recipient"
        >
          <UserPlus className="size-4" />
        </button>
      </div>

      {open ? (
        <section className="panel animate-tick space-y-2.5 p-4">
          <p className="label-instrument">{editing ? "Edit recipient" : "New recipient"}</p>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value.slice(0, 40))}
            placeholder="Label — e.g. Cold wallet"
            className="field h-11 w-full px-3"
          />
          <input
            value={address}
            onChange={(e) => setAddress(e.target.value.trim())}
            placeholder="r… destination address"
            className="field h-11 w-full px-3 text-[12px]"
          />
          {address ? (
            <p className="readout text-[10px] text-faint">
              Fingerprint <span className="text-accent">{addressFingerprint(address)}</span>
            </p>
          ) : null}
          <input
            value={tag}
            inputMode="numeric"
            onChange={(e) => setTag(e.target.value.replace(/[^0-9]/g, "").slice(0, 10))}
            placeholder="Destination tag (exchanges require this)"
            className="field h-11 w-full px-3"
          />
          <input
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 140))}
            placeholder="Note (optional)"
            className="field h-11 w-full px-3"
          />
          <div className="flex gap-2">
            <button type="button" onClick={reset} className="btn-quiet h-10 flex-1">
              Cancel
            </button>
            <button
              type="button"
              disabled={busy || !label.trim() || !address.trim()}
              onClick={() => void submit()}
              className="btn-accent h-10 flex-[2] disabled:opacity-40"
            >
              {busy ? "Checking…" : "Save recipient"}
            </button>
          </div>
        </section>
      ) : null}

      {contacts.length > 3 ? (
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-faint" />
          <input
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Search recipients"
            className="field h-10 w-full pl-9 pr-3 text-[12px]"
          />
        </div>
      ) : null}

      {filtered.length === 0 ? (
        <div className="panel grid place-items-center gap-2 px-6 py-10 text-center">
          <Users className="size-5 text-faint" />
          <p className="readout text-[11px] leading-relaxed text-faint">
            {contacts.length === 0
              ? "No saved recipients yet. Save one to skip retyping addresses and tags."
              : "No recipients match that search."}
          </p>
        </div>
      ) : (
        <ul className="panel divide-y divide-border">
          {filtered.map((c) => (
            <li key={c.id} className="p-3">
              <div className="flex items-start gap-3">
                <span className="grid size-9 shrink-0 place-items-center rounded-lg accent-tint text-[11px] font-medium">
                  {c.label.slice(0, 2).toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px]">{c.label}</p>
                  <p className="readout mt-0.5 text-[10px] text-faint">
                    {truncateAddress(c.address, 6, 6)}
                    {c.tag ? ` · tag ${c.tag}` : ""}
                  </p>
                  <p className="readout mt-0.5 text-[10px] text-accent">
                    {addressFingerprint(c.address)}
                  </p>
                  {c.note ? (
                    <p className="readout mt-1 text-[10px] leading-relaxed text-muted">{c.note}</p>
                  ) : null}
                </div>
              </div>
              <div className="mt-2.5 flex gap-2">
                <button
                  type="button"
                  onClick={() =>
                    void navigate({
                      to: "/send",
                      search: { to: c.address, ...(c.tag ? { tag: c.tag } : {}) } as { to?: string; tag?: string },
                    })
                  }
                  className="btn-outline-accent flex h-8 flex-1 items-center justify-center gap-1.5"
                >
                  <Send className="size-3" /> Send
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditing(c.id);
                    setLabel(c.label);
                    setAddress(c.address);
                    setTag(c.tag ?? "");
                    setNote(c.note ?? "");
                    setOpen(true);
                  }}
                  className="btn-quiet h-8 flex-1"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => {
                    removeContact(c.id);
                    toast.success("Recipient removed.");
                  }}
                  aria-label={`Remove ${c.label}`}
                  className="btn-quiet grid size-8 place-items-center"
                >
                  <Trash2 className="size-3" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
